import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, FormHelperText, FormLabel, Grid, IconButton, LinearProgress, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Control, Controller } from 'react-hook-form';
import ReactPlayer from 'react-player';

interface VideoValidation {
    maxSizeInMB?: number;
    maxDurationSeconds?: number;
    maxCount?: number;
    acceptedFormats?: string[];
    aspectRatio?: number;
}

interface InputVideosProps {
    name: string;
    label: string;
    control: Control<any>;
    validation?: VideoValidation;
    helpText?: string;
    required?: boolean;
    viewOnly?: boolean;
    existingFiles?: Array<{
        _id?: string;
        url?: string;
        name?: string;
        size?: number;
        mimetype?: string;
    }>;
}

interface VideoItem {
    id: string;
    file?: File;
    url: string;
    name: string;
    size: number;
    duration?: number;
    isExisting?: boolean;
    existingId?: string;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            window.URL.revokeObjectURL(video.src);
            resolve(video.duration);
        };
        video.onerror = () => reject('Failed to load video metadata');
        video.src = URL.createObjectURL(file);
    });
};

interface DropzoneUploadProps {
    onDrop: (files: FileList) => void;
    disabled?: boolean;
    aspectRatio?: number;
    label?: string;
}

const PREVIEW_BORDER_RADIUS = '12px';

const DropzoneUpload: React.FC<DropzoneUploadProps> = ({ onDrop, disabled, aspectRatio = 16 / 9, label }) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles) => {
            const dataTransfer = new DataTransfer();
            acceptedFiles.forEach(file => dataTransfer.items.add(file));
            onDrop(dataTransfer.files);
        },
        accept: {
            'video/*': ['.mp4', '.mov', '.avi', '.webm', '.mkv']
        },
        multiple: true,
        disabled,
    });

    return (
        <Card sx={{ position: 'relative', borderRadius: PREVIEW_BORDER_RADIUS }}>
            <Box
                {...getRootProps()}
                sx={(theme) => ({
                    position: 'relative',
                    borderRadius: PREVIEW_BORDER_RADIUS,
                    width: '100%',
                    paddingTop: `${(1 / aspectRatio) * 100}%`,
                    overflow: 'hidden',
                    border: isDragActive ? '2px dashed' : 'none',
                    borderColor: isDragActive ? theme.palette.primary.main : 'transparent',
                    backgroundColor: theme.palette.mode === 'dark' ? '#282C30' : '#F9F9F9',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: disabled ? 0.5 : 1,
                    '&:hover': disabled ? {} : {
                        backgroundColor: theme.palette.mode === 'dark' ? '#2f3439' : '#f0f0f0',
                    },
                })}
            >
                <input {...getInputProps()} />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 2,
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: '14px',
                            fontWeight: 500,
                        }}
                    >
                        {isDragActive ? 'Déposez les vidéos ici...' : label || 'Glissez-déposez des vidéos ici'}
                    </Typography>
                </Box>
            </Box>
        </Card>
    );
};

const InputVideos: React.FC<InputVideosProps> = ({
    name,
    label,
    control,
    validation,
    helpText,
    required = false,
    viewOnly = false,
    existingFiles = []
}) => {
    const theme = useTheme();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewVideo, setPreviewVideo] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const validateFile = async (file: File): Promise<string | null> => {
        if (!validation) return null;

        // Size validation
        if (validation.maxSizeInMB && file.size > validation.maxSizeInMB * 1024 * 1024) {
            return `La taille du fichier doit être inférieure à ${validation.maxSizeInMB}Mo`;
        }

        // Type validation
        if (!file.type.startsWith('video/')) {
            return 'Le fichier doit être une vidéo';
        }

        // Format validation
        if (validation.acceptedFormats && 
            !validation.acceptedFormats.some(format => file.type.includes(format))) {
            return `Formats acceptés: ${validation.acceptedFormats.join(', ')}`;
        }

        // Duration validation
        if (validation.maxDurationSeconds) {
            try {
                const duration = await getVideoDuration(file);
                if (duration > validation.maxDurationSeconds) {
                    return `La durée de la vidéo doit être inférieure à ${validation.maxDurationSeconds} secondes`;
                }
            } catch (err) {
                return 'Échec de la validation de la durée de la vidéo';
            }
        }

        return null;
    };

    const convertExistingToVideoItems = (): VideoItem[] => {
        return existingFiles.map((file, index) => ({
            id: file._id || `existing-${index}`,
            url: file.url || '',
            name: file.name || `Vidéo ${index + 1}`,
            size: file.size || 0,
            isExisting: true,
            existingId: file._id,
        }));
    };

    const convertVideoItemsToFormData = (items: VideoItem[]) => {
        return items.map(item => {
            if (item.isExisting) {
                return {
                    _id: item.existingId,
                    url: item.url,
                    name: item.name,
                    size: item.size
                };
            } else {
                return item.file;
            }
        }).filter(Boolean);
    };

    const handleFilesSelect = async (files: FileList, currentValue: any[], onChange: (value: any[]) => void) => {
        setLoading(true);
        setError(null);

        const currentItems: VideoItem[] = Array.isArray(currentValue) 
            ? currentValue.map((item): VideoItem => {
                if (item instanceof File) {
                    return {
                        id: `file-${Date.now()}-${Math.random()}`,
                        file: item,
                        url: URL.createObjectURL(item),
                        name: item.name,
                        size: item.size,
                        isExisting: false
                    };
                } else {
                    return {
                        id: item._id || `existing-${Date.now()}-${Math.random()}`,
                        url: item.url || '',
                        name: item.name || 'Video',
                        size: item.size || 0,
                        isExisting: true,
                        existingId: item._id
                    };
                }
            })
            : convertExistingToVideoItems();

        const newItems: VideoItem[] = [];

        for (const file of Array.from(files)) {
            // Validate count
            if (validation?.maxCount && 
                currentItems.length + newItems.length >= validation.maxCount) {
                setError(`Maximum ${validation.maxCount} vidéos autorisées`);
                break;
            }

            const validationError = await validateFile(file);
            if (validationError) {
                setError(validationError);
                break;
            }

            let duration: number | undefined;
            try {
                duration = await getVideoDuration(file);
            } catch (err) {
                // Duration is optional, continue without it
            }

            newItems.push({
                id: `file-${Date.now()}-${Math.random()}`,
                file,
                url: URL.createObjectURL(file),
                name: file.name,
                size: file.size,
                duration,
                isExisting: false
            });
        }

        const updatedItems = [...currentItems, ...newItems];
        onChange(convertVideoItemsToFormData(updatedItems));
        setLoading(false);
    };

    const handleRemoveVideo = (indexToRemove: number, currentValue: any[], onChange: (value: any[]) => void) => {
        const currentItems: VideoItem[] = Array.isArray(currentValue) 
            ? currentValue.map((item): VideoItem => {
                if (item instanceof File) {
                    return {
                        id: `file-${Date.now()}-${Math.random()}`,
                        file: item,
                        url: URL.createObjectURL(item),
                        name: item.name,
                        size: item.size,
                        isExisting: false
                    };
                } else {
                    return {
                        id: item._id || `existing-${Date.now()}-${Math.random()}`,
                        url: item.url || '',
                        name: item.name || 'Video',
                        size: item.size || 0,
                        isExisting: true,
                        existingId: item._id
                    };
                }
            })
            : convertExistingToVideoItems();

        const updatedItems = currentItems.filter((_, index) => index !== indexToRemove);
        onChange(convertVideoItemsToFormData(updatedItems));
    };

    const handlePreview = (url: string) => {
        setPreviewVideo(url);
        setPreviewOpen(true);
    };

    const renderVideoMeta = (item: VideoItem) => {
        if (viewOnly) return null;

        return (
            <Box sx={{ px: 0.5, pt: 1 }}>
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {item.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatFileSize(item.size)}
                </Typography>
            </Box>
        );
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: required ? `${label} est requis` : false }}
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => {
                const currentItems: VideoItem[] = Array.isArray(value) 
                    ? value.map((item): VideoItem => {
                        if (item instanceof File) {
                            return {
                                id: `file-${Date.now()}-${Math.random()}`,
                                file: item,
                                url: URL.createObjectURL(item),
                                name: item.name,
                                size: item.size,
                                isExisting: false
                            };
                        } else {
                            return {
                                id: item._id || `existing-${Date.now()}-${Math.random()}`,
                                url: item.url || '',
                                name: item.name || 'Video',
                                size: item.size || 0,
                                isExisting: true,
                                existingId: item._id
                            };
                        }
                    })
                    : convertExistingToVideoItems();

                return (
                    <FormGroup>
                        <FormLabel required={required}>{label}</FormLabel>

                        {helpText && (
                            <FormHelperText>{helpText}</FormHelperText>
                        )}

                        {loading && <LinearProgress sx={{ mb: 2 }} />}

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {/* Left column - First video */}
                            <Grid item xs={12} sm={6}>
                                {currentItems.length > 0 ? (
                                    <Box>
                                        <Card sx={{ position: 'relative', height: '100%', borderRadius: PREVIEW_BORDER_RADIUS }}>
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    paddingTop: `${(1 / (validation?.aspectRatio || 16 / 9)) * 100}%`,
                                                    overflow: 'hidden',
                                                    bgcolor: 'grey.100',
                                                    borderRadius: PREVIEW_BORDER_RADIUS,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: PREVIEW_BORDER_RADIUS,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            '& video': {
                                                                objectFit: 'cover',
                                                                width: '100% !important',
                                                                height: '100% !important',
                                                            }
                                                        }}
                                                    >
                                                        <ReactPlayer
                                                            url={currentItems[0].url}
                                                            width="100%"
                                                            height="100%"
                                                            muted
                                                            playing={false}
                                                            light={true}
                                                            playIcon={<Box />}
                                                            config={{
                                                                file: {
                                                                    attributes: {
                                                                        style: { 
                                                                            width: '100%', 
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            borderRadius: PREVIEW_BORDER_RADIUS,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            bgcolor: 'rgba(0,0,0,0.3)',
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handlePreview(currentItems[0].url)}
                                                    >
                                                        <PlayArrowIcon sx={{ color: 'white', fontSize: 48 }} />
                                                    </Box>
                                                </Box>
                                                {!viewOnly && (
                                                    <IconButton
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            zIndex: 10
                                                        }}
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleRemoveVideo(0, value || [], onChange)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Card>
                                        {renderVideoMeta(currentItems[0])}
                                    </Box>
                                ) : (
                                    !viewOnly && !(validation?.maxCount && currentItems.length >= validation.maxCount) && (
                                        <DropzoneUpload
                                            onDrop={(files) => handleFilesSelect(files, value || [], onChange)}
                                            disabled={loading}
                                            aspectRatio={validation?.aspectRatio || 16 / 9}
                                            label="Glissez-déposez une vidéo ici"
                                        />
                                    )
                                )}
                            </Grid>

                            {/* Right column - 2x2 grid of up to 4 videos */}
                            <Grid item xs={12} sm={6}>
                                <Grid container spacing={2}>
                                    {currentItems.slice(1, 5).map((item, index) => (
                                        <Grid item xs={6} key={item.id}>
                                            <Box>
                                                <Card sx={{ position: 'relative', height: '100%', borderRadius: PREVIEW_BORDER_RADIUS }}>
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            paddingTop: `${(1 / (validation?.aspectRatio || 16 / 9)) * 100}%`,
                                                            overflow: 'hidden',
                                                            bgcolor: 'grey.100',
                                                            borderRadius: PREVIEW_BORDER_RADIUS,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                borderRadius: PREVIEW_BORDER_RADIUS,
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    '& video': {
                                                                        objectFit: 'cover',
                                                                        width: '100% !important',
                                                                        height: '100% !important',
                                                                    }
                                                                }}
                                                            >
                                                                <ReactPlayer
                                                                    url={item.url}
                                                                    width="100%"
                                                                    height="100%"
                                                                    muted
                                                                    playing={false}
                                                                    light={true}
                                                                    playIcon={<Box />}
                                                                    config={{
                                                                        file: {
                                                                            attributes: {
                                                                                style: { 
                                                                                    width: '100%', 
                                                                                    height: '100%',
                                                                                    objectFit: 'cover'
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    bottom: 0,
                                                                    borderRadius: PREVIEW_BORDER_RADIUS,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: 'rgba(0,0,0,0.3)',
                                                                    cursor: 'pointer'
                                                                }}
                                                                onClick={() => handlePreview(item.url)}
                                                            >
                                                                <PlayArrowIcon sx={{ color: 'white', fontSize: 48 }} />
                                                            </Box>
                                                            {/* "More" overlay on 4th video if there are more videos */}
                                                            {index === 3 && currentItems.length > 5 && (
                                                                <Box
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        bottom: 0,
                                                                        borderRadius: PREVIEW_BORDER_RADIUS,
                                                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        zIndex: 5
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="h5"
                                                                        sx={{
                                                                            color: 'white',
                                                                            fontWeight: 600
                                                                        }}
                                                                    >
                                                                        +{currentItems.length - 5} more
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                        {!viewOnly && (
                                                            <IconButton
                                                                sx={{
                                                                    position: 'absolute',
                                                                    top: 8,
                                                                    right: 8,
                                                                    zIndex: 10
                                                                }}
                                                                color="error"
                                                                size="small"
                                                                onClick={() => handleRemoveVideo(index + 1, value || [], onChange)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                </Card>
                                                {renderVideoMeta(item)}
                                            </Box>
                                        </Grid>
                                    ))}

                                    {/* Add video button slot */}
                                    {currentItems.length > 0 && currentItems.length < (validation?.maxCount || Infinity) && (
                                        currentItems.slice(1, 5).length < 4 && (
                                            <Grid item xs={6}>
                                                {!viewOnly && !(validation?.maxCount && currentItems.length >= validation.maxCount) && (
                                                    <DropzoneUpload
                                                        onDrop={(files) => handleFilesSelect(files, value || [], onChange)}
                                                        disabled={loading}
                                                        aspectRatio={validation?.aspectRatio || 16 / 9}
                                                        label="Ajouter"
                                                    />
                                                )}
                                            </Grid>
                                        )
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>

                        {validation?.maxCount && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                {currentItems.length} / {validation.maxCount} vidéos
                            </Typography>
                        )}

                        {(error || fieldError) && (
                            <FormHelperText error>
                                {error || fieldError?.message}
                            </FormHelperText>
                        )}

                        {/* Video Preview Dialog */}
                        <Dialog
                            open={previewOpen}
                            onClose={() => setPreviewOpen(false)}
                            maxWidth="md"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    bgcolor: theme.palette.mode === 'dark' ? '#242629' : theme.palette.background.paper,
                                }
                            }}
                        >
                            <DialogTitle
                                sx={{
                                    color: theme.palette.text.primary,
                                }}
                            >
                                Aperçu de la vidéo
                            </DialogTitle>
                            <DialogContent>
                                {previewVideo && (
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            paddingTop: '56.25%', // 16:9 aspect ratio
                                            bgcolor: theme.palette.mode === 'dark' ? '#000' : '#f5f5f5',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                '& video': {
                                                    objectFit: 'contain',
                                                    width: '100% !important',
                                                    height: '100% !important',
                                                }
                                            }}
                                        >
                                            <ReactPlayer
                                                url={previewVideo}
                                                width="100%"
                                                height="100%"
                                                controls
                                                playing
                                                config={{
                                                    file: {
                                                        attributes: {
                                                            style: { 
                                                                width: '100%', 
                                                                height: '100%',
                                                                objectFit: 'contain'
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setPreviewOpen(false)}>Fermer</Button>
                            </DialogActions>
                        </Dialog>
                    </FormGroup>
                );
            }}
        />
    );
};

export default InputVideos;
